import React, {
    ReactElement,
} from "react";
import {
    ChatSettingsSlack,
    ChatSettingsTag,
} from "../../../../../__generated__/api";
import { ReactComponent as SlackLogo } from "../../../../../assets/img/slack-logo.svg";
import Editor from "../../../../../components/agent-v3/editor/Editor";
import { ProxyProperty } from "../../../../../components/dirty-tracker/FormTracker";
import AdvancedSection from "../../../../../components/expanding-section/AdvancedSection";
import ChannelModePicker from "../../../../../components/pickers/channel-mode/ChannelMode";
import ChannelTags from "../../../../../components/pickers/channel-tags/ChannelTags";
import SlackBots from "../../../../../components/pickers/slack-bots/SlackBots";
import TagList from "../../../../../components/tag-list/TagList";
import SettingsContainer from "../../../../../components/theme/settings";
import Toggle from "../../../../../components/theme/toggle/Toggle";
import useRouting, { Link } from "../../../../../contexts/Routing";
import { useSetTitle } from "../../base";
import ChannelModeHelp from "../../channels/slack/ChannelModeHelp";
import { TICKET_CREATED_MESSAGE_VARIABLES } from "../../variables";
import Styles from "../Chat.module.scss";

export interface Props {
    data: ProxyProperty<ChatSettingsSlack>,
    tag: ChatSettingsTag | undefined | null;
}

export default function SlackSettings({
    data,
    tag,
}: Props): ReactElement {
    const { Paths } = useRouting();

    const properties = data.useBuilder(builder => builder
        .array("tags", current => current?.tags ?? [])
        .property("channelMode", current => current?.channelMode)
        .array("approvedBotIds", current => current?.approvedBotIds as string[] ?? [])
        .array("ignoreTicketsFromEmailDomains", current => current?.ignoreTicketsFromEmailDomains ?? [])
        .property("ticketCreatedTemplate", current => current?.ticketCreatedTemplate ?? "")
        .property("canFillOutForm", current => current?.canFillOutForm)
        .array("emojiActions", current => current?.emojiActions ?? [])
        .property("name", current => current?.name ?? ""));

    useSetTitle(!tag && data?.value?.name ? (
        <>
            <SlackLogo className={Styles["nav-icon"]} />
            {" "}
            #
            {data?.value.name}
        </>
    ) : undefined, [data]);

    const advancedSettings = (
        <>

            <SettingsContainer.Setting
                title="Channel Mode"
                description="When a user types a messages in a channel, should that create a new
                        ticket and thread, or should ticket creation be done manually using features such
                        as emoji actions or the channel menu?"
                descriptionElement={(
                    <>
                        When a user types a messages in a channel, should that create a new
                        ticket and thread, or should ticket creation be done manually using features such
                        as emoji actions or the channel menu?
                        {" "}
                        <ChannelModeHelp iconVariant="default" iconSize="sm" />
                    </>

                )}
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
            >
                <ChannelModePicker
                    className={SettingsContainer.Setting.PickerClassName}
                    onChange={properties.channelMode.setValue}
                    value={properties.channelMode.value}
                    isClearable
                />
            </SettingsContainer.Setting>

            <SettingsContainer.Setting
                title="Ignore Internal Messages"
                description="If your team frequently sends content in customer
                    channels, and you don&apos;t want tickets created for these, add their email or domain
                    (e.g., @example.com) here. This will prevent ticket creation for matching users,
                    overriding the general configuration.
                    This setting overrides the same setting in the general configuration.
                    If any domains or emails are present in this list, we will ignore the general setting."
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
            >
                <TagList
                    placeholder="@example.com"
                    value={properties.ignoreTicketsFromEmailDomains.value}
                    onChange={properties.ignoreTicketsFromEmailDomains.setValue}
                    canNotHaveDuplicates
                />
            </SettingsContainer.Setting>

            <SettingsContainer.Setting
                title="Approved Bots"
                description="By default, Agent always blocks messages from other bots in Slack. However, you can
                    override this behavior and have Agent injest messages and create tickets from other
                    bots in your Slack workspace"
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
            >
                <SlackBots
                    isMulti
                    placeholder="Which bot messages should not be ignored."
                    value={properties.approvedBotIds.value}
                    onSelected={properties.approvedBotIds.setValue}
                />
            </SettingsContainer.Setting>

            <SettingsContainer.Setting
                title="Ticket Created Message Template"
                description="When a ticket is created and a new thread is posted in the channel,
                    what information should this ticket contain?"
                descriptionElement={(
                    <>
                        When a ticket is created and a new thread is posted in the channel,
                        what information should this ticket contain?
                    </>
                )}
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
            >
                <Editor
                    value={properties.ticketCreatedTemplate.value}
                    onChange={properties.ticketCreatedTemplate.setValue}
                    size="medium"
                    hasBorder
                    placeholder="Hey, please remember to use a thread..."
                    variables={TICKET_CREATED_MESSAGE_VARIABLES}
                />
            </SettingsContainer.Setting>

            <SettingsContainer.Setting
                title="Fill out form prompt"
                description=" When creating a ticket, if a request type is specified in a channel, the will see a
                    &quot;Fill out form&quot;
                    button to provide more context and fill out the request type. However, in the case of
                    a channel menu, you may not want that option. By setting this to No, users will not see
                    the option to fill out the request."
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
            >
                <Toggle
                    checked={properties.canFillOutForm.value as boolean}
                    onChange={event => properties.canFillOutForm.setValue(event.target.checked)}
                />

            </SettingsContainer.Setting>

        </>
    );

    return (
        <SettingsContainer.Section
            title="Slack Settings"
            helpText="These general settings help you manage this specific channel and its overrides"
        >
            {!tag && (

                <SettingsContainer.Setting
                    title="Channel Groups"
                    description="Which channel groups should be associated with this channel.
                To manage channel channel groups, visit Channel Group Settings"
                    descriptionElement={(
                        <>
                            Which channel groups should be associated with this channel.
                            To manage channel channel groups, visit
                            {" "}
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channel-groups" }}
                            >
                                Channel Group Settings
                            </Link>
                        </>

                    )}
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                >
                    <ChannelTags
                        placeholder="Please pick a list of channel tags"
                        isMulti
                        onSelected={properties.tags.setValue}
                        value={properties.tags.value}
                        canCreate
                    />
                </SettingsContainer.Setting>
            )}

            {tag ? advancedSettings : (
                <AdvancedSection title="Channel Overrides">
                    {advancedSettings}
                </AdvancedSection>
            )}
        </SettingsContainer.Section>
    );
}
